import { gsap } from 'gsap';
import CustomEase from 'gsap/dist/CustomEase';
import ScrollTrigger from 'gsap/dist/ScrollTrigger';
import React, { useRef } from 'react';
import { customEase, headerHeight } from 'src/components/utils/gsapVariables';

import type { SelectOption } from '@/components/elements';
import type { ITextRadioProps } from '@/components/elements';
import {
  useIsGreaterThan,
  useIsomorphicLayoutEffect,
  useIsSmallerThan,
} from '@/components/utils';
import { type TrackingInfo } from '@/lib';

import { SearchForm } from '../SearchForm';
import { type SuggestionTeaserItem } from '../SuggestionRow';
import {
  StyledHeader,
  StyledHeadline,
  StyledSearchHero,
  StyledSuggestionRow,
} from './SearchHero.styles';

interface ISearchHeroProps {
  className?: string;
  headline: string;
  smallImage: React.ReactNode;
  largeImage: React.ReactNode;
  items?: SuggestionTeaserItem[];
  searchForm: {
    placeholder: string;
    propertyTypeOptions: SelectOption[];
    propertyTypeButtonLabel: string;
    marketingTypeOptions: ITextRadioProps[];
    marketingTypeLabel: string;
    trackingInfo?: TrackingInfo;
  };
  hasAnimation?: boolean;
}

export const SearchHero = ({
  className,
  headline,
  smallImage,
  largeImage,
  items,
  searchForm,
  hasAnimation,
}: ISearchHeroProps) => {
  const searchHeroRef = useRef(null);
  const suggestionRowRef = useRef(null);
  const imageRef = useRef(null);
  const isMobile = useIsSmallerThan('TABLET_PORTRAIT');
  const isTabletLandscape = useIsGreaterThan('TABLET_LANDSCAPE');
  const isLaptop = useIsGreaterThan('LAPTOP');

  const image = isMobile ? smallImage : largeImage;
  const {
    placeholder,
    propertyTypeOptions,
    propertyTypeButtonLabel,
    marketingTypeOptions,
    marketingTypeLabel,
    trackingInfo,
  } = searchForm;

  useIsomorphicLayoutEffect(() => {
    if (!isTabletLandscape || !hasAnimation) return;

    gsap.registerPlugin(ScrollTrigger, CustomEase);

    CustomEase.create('customEase', customEase);

    const ctx = gsap.context(() => {
      const tl = gsap.timeline({});

      gsap.from(suggestionRowRef.current, {
        scrollTrigger: {
          trigger: suggestionRowRef.current,
          start: isLaptop ? 'top bottom' : 'top-=50% bottom',
          end: '+=45%',
          toggleActions: 'restart play resume reverse',
        },
        y: '50%',
        opacity: 0,
        duration: 0.8,
        ease: 'customEase',
      });

      tl.to(imageRef.current, {
        scrollTrigger: {
          trigger: searchHeroRef.current,
          start: `top ${headerHeight}`,
          end: '+=25%',
          scrub: true,
        },
        y: -16,
        ease: 'customEase',
      });

      tl.to(searchHeroRef.current, {
        scrollTrigger: {
          trigger: searchHeroRef.current,
          start: `top ${headerHeight}`,
          end: '+=25%',
          scrub: true,
        },
        y: -16,
        ease: 'customEase',
      });
    }, searchHeroRef);

    return () => ctx.revert();
  }, [isTabletLandscape, isLaptop, hasAnimation]);

  return (
    <StyledSearchHero
      className={className}
      forwardedAs="section"
      width={{ laptop: 'xl', tabletLandscape: 'l' }}
      $hasTeaser={Boolean(items)}
      ref={searchHeroRef}
    >
      <StyledHeader>
        <StyledHeadline size="h1" forwardedAs="p" label={headline} />
        <SearchForm
          placeholder={placeholder}
          propertyTypeOptions={propertyTypeOptions}
          propertyTypeButtonLabel={propertyTypeButtonLabel}
          marketingTypeOptions={marketingTypeOptions}
          marketingTypeLabel={marketingTypeLabel}
          trackingInfo={trackingInfo}
        />
      </StyledHeader>
      <div ref={imageRef}>{image}</div>
      {items && (
        <StyledSuggestionRow tag="div" items={items} ref={suggestionRowRef} />
      )}
    </StyledSearchHero>
  );
};
